import React, { useEffect, useState, useMemo, useRef } from 'react';
import { View, Text, Platform, Pressable, ScrollView } from 'react-native';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { getContextSize, getContextBreakdown, getSummaryEntries, clearContext, getContextEntries, setupSocketListener, ContextBreakdown, ContextEntry } from '@services/contextTracker';
import * as apis from '@api/api';
import { LinearGradient } from 'expo-linear-gradient';
import { Dialog, Adapt, Sheet, YStack, XStack, Button } from 'tamagui';
import { useAlertDialog } from '@components/Global/AlertDialog/useAlertDialog';
// HoverLabel component for tooltip (web-optimized)
const HoverLabel = ({ label, children, position = 'bottom', onChildClick }: { label: React.ReactNode; children: React.ReactNode; position?: 'top' | 'bottom' | 'left' | 'right'; onChildClick?: () => void }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  if (Platform.OS !== 'web') {
    return <>{children}</>;
  }

  useEffect(() => {
    if (!isVisible || !containerRef.current || !tooltipRef.current) return;

    const updatePosition = () => {
      const container = containerRef.current;
      const tooltip = tooltipRef.current;
      if (!container || !tooltip) return;

      const rect = container.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const margin = 12;
      const estimatedWidth = 320; // Approximate tooltip width
      const estimatedHeight = tooltipRect.height || 200; // Use actual height if available

      let style: React.CSSProperties = {
        position: 'fixed',
        padding: '0',
        borderRadius: '12px',
        zIndex: 99999,
        minWidth: '280px',
        maxWidth: '360px',
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(135, 206, 235, 0.1)',
        pointerEvents: 'none',
        overflow: 'visible',
      };

      if (position === 'bottom') {
        let left = rect.left + (rect.width / 2) - (estimatedWidth / 2) + 100;
        let top = rect.bottom + margin;

        // Keep tooltip within viewport
        if (left < margin) left = margin;
        if (left + estimatedWidth > viewportWidth - margin) {
          left = viewportWidth - estimatedWidth - margin;
        }
        if (top + estimatedHeight > viewportHeight - margin) {
          // Flip to top if no room below
          top = rect.top - estimatedHeight - margin;
        }

        style.left = `${Math.max(margin, left)}px`;
        style.top = `${top}px`;
      } else if (position === 'top') {
        let left = rect.left + (rect.width / 2) - (estimatedWidth / 2);
        let top = rect.top - estimatedHeight - margin;

        if (left < margin) left = margin;
        if (left + estimatedWidth > viewportWidth - margin) {
          left = viewportWidth - estimatedWidth - margin;
        }
        if (top < margin) {
          // Flip to bottom if no room above
          top = rect.bottom + margin;
        }

        style.left = `${Math.max(margin, left)}px`;
        style.top = `${top}px`;
      } else if (position === 'right') {
        let left = rect.right + margin;
        let top = rect.top + (rect.height / 2) - (estimatedHeight / 2);

        if (left + estimatedWidth > viewportWidth - margin) {
          // Flip to left if no room on right
          left = rect.left - estimatedWidth - margin;
        }
        if (top < margin) top = margin;
        if (top + estimatedHeight > viewportHeight - margin) {
          top = viewportHeight - estimatedHeight - margin;
        }

        style.left = `${left}px`;
        style.top = `${top}px`;
      } else { // left
        let right = viewportWidth - rect.left + margin;
        let top = rect.top + (rect.height / 2) - (estimatedHeight / 2);

        if (right + estimatedWidth > viewportWidth - margin) {
          // Flip to right if no room on left
          right = viewportWidth - rect.right - margin;
          style.right = `${right}px`;
          style.left = 'auto';
        } else {
          style.right = `${right}px`;
          style.left = 'auto';
        }

        if (top < margin) top = margin;
        if (top + estimatedHeight > viewportHeight - margin) {
          top = viewportHeight - estimatedHeight - margin;
        }

        style.top = `${top}px`;
      }

      setTooltipStyle(style);
    };

    // Initial positioning
    updatePosition();

    // Update on scroll/resize
    const handleUpdate = () => updatePosition();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    // Recalculate after a short delay to get actual tooltip dimensions
    const timeout = setTimeout(() => {
      if (tooltipRef.current) {
        const actualRect = tooltipRef.current.getBoundingClientRect();
        updatePosition();
      }
    }, 10);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
      clearTimeout(timeout);
    };
  }, [isVisible, position]);

  // Render children and tooltip using React portals or direct rendering
  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      onClick={(e) => {
        // Allow clicks to pass through to children
        if (onChildClick && (e.target === e.currentTarget || (e.target as HTMLElement).closest('[data-clickable]'))) {
          onChildClick();
        }
      }}
    >
      {children}
      {isVisible && (
        <div
          ref={tooltipRef}
          style={{
            ...tooltipStyle,
            animation: 'fadeIn 0.2s ease-out',
          } as React.CSSProperties}
          onMouseEnter={() => setIsVisible(true)}
          onMouseLeave={() => setIsVisible(false)}
        >
          <style>{`
            @keyframes fadeIn {
              from {
                opacity: 0;
                transform: translateY(-4px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}</style>
          {typeof label === 'string' ? (
            <span style={{ color: 'white', fontSize: '13px', fontWeight: '600', display: 'block', padding: '12px' }}>
              {label}
            </span>
          ) : (
            <div style={{ pointerEvents: 'auto' }}>
              {label}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ApexCharts for web - use conditional import
let ReactApexChart: any = null;
if (Platform.OS === 'web') {
  try {
    // Use the same import pattern as ApexChartsComponents.web.tsx
    const apexModule = require('react-apexcharts');
    ReactApexChart = apexModule.default || apexModule;
    if (ReactApexChart) {
      console.log('[ContextProgressRing] ApexCharts loaded successfully');
    } else {
      console.warn('[ContextProgressRing] ApexCharts module loaded but default export is null');
    }
  } catch (error) {
    console.warn('[ContextProgressRing] ApexCharts require failed:', error);
    ReactApexChart = null;
  }
}

const CONTEXT_LIMIT_TOKENS = 200000;
const CONTEXT_LIMIT_BYTES = 200000;

// Hero gradient colors
const GRADIENT_COLORS = {
  start: '#C8A2C8', // Purple
  end: '#87CEEB'    // Sky blue
};

// Category colors (derived from gradient)
const CATEGORY_COLORS = {
  user_interaction: '#C8A2C8',      // Purple
  notification: '#A8B5D0',           // Blue-purple blend
  ai_request: '#87CEEB',             // Sky blue
  tensorflow_request: '#B89DD4',     // Purple-pink blend
  document_viewing: '#9BB5CE'        // Blue-gray blend
};

interface ContextProgressRingProps {
  size?: number;
}

export const ContextProgressRing: React.FC<ContextProgressRingProps> = ({ size = 50 }) => {
  const theme = themeSelector();
  const { showAlert } = useAlertDialog();
  const [contextSize, setContextSize] = useState({ tokens: 0, bytes: 0 });
  const [breakdown, setBreakdown] = useState<ContextBreakdown>({
    user_interaction: { tokens: 0, bytes: 0, count: 0 },
    notification: { tokens: 0, bytes: 0, count: 0 },
    ai_request: { tokens: 0, bytes: 0, count: 0 },
    tensorflow_request: { tokens: 0, bytes: 0, count: 0 },
    document_viewing: { tokens: 0, bytes: 0, count: 0 }
  });
  const [isHovered, setIsHovered] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryEntries, setSummaryEntries] = useState<ContextEntry[]>([]);
  const [apexChartsLoaded, setApexChartsLoaded] = useState(ReactApexChart !== null);
  const [isClearing, setIsClearing] = useState(false);
  const [isSummarizing, setIsSummarizing] = useState(false);

  // Try to load ApexCharts dynamically if not already loaded
  useEffect(() => {
    if (Platform.OS === 'web') {
      if (ReactApexChart && !apexChartsLoaded) {
        // ApexCharts was loaded at module level
        setApexChartsLoaded(true);
      } else if (!ReactApexChart && !apexChartsLoaded) {
        // Try dynamic import as fallback
        import('react-apexcharts')
          .then((module) => {
            const ApexChart = module.default || module;
            if (ApexChart) {
              // Update the module-level variable and window for access
              (window as any).__ReactApexChart = ApexChart;
              // Force re-render by updating state
              setApexChartsLoaded(true);
              console.log('[ContextProgressRing] ApexCharts loaded via dynamic import');
            }
          })
          .catch((error) => {
            console.warn('[ContextProgressRing] Failed to load ApexCharts dynamically:', error);
          });
      }
    }
  }, [apexChartsLoaded]);

  // Load summary entries when modal opens
  useEffect(() => {
    if (showSummaryModal && Platform.OS === 'web') {
      const loadSummaries = async () => {
        try {
          const summaries = await getSummaryEntries();
          setSummaryEntries(summaries);
        } catch (error) {
          console.error('Failed to load summary entries:', error);
          setSummaryEntries([]);
        }
      };
      loadSummaries();
    }
  }, [showSummaryModal]);

  // Update context size and breakdown periodically (optimized)
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    let isMounted = true;
    let intervalId: NodeJS.Timeout | null = null;

    const updateContext = async () => {
      if (!isMounted) return;

      try {
        const [sizeData, breakdownData] = await Promise.all([
          getContextSize(),
          getContextBreakdown()
        ]);

        if (!isMounted) return; // Check again after async operations

        // Only update state if values actually changed to prevent unnecessary re-renders
        setContextSize(prev => {
          if (prev.tokens === sizeData.tokens && prev.bytes === sizeData.bytes) {
            return prev;
          }
          return sizeData;
        });

        setBreakdown(prev => {
          const hasChanged = Object.keys(breakdownData).some(key => {
            const prevData = prev[key as keyof ContextBreakdown];
            const newData = breakdownData[key as keyof ContextBreakdown];
            return prevData.tokens !== newData.tokens ||
              prevData.bytes !== newData.bytes ||
              prevData.count !== newData.count;
          });
          return hasChanged ? breakdownData : prev;
        });
      } catch (error) {
        if (isMounted) {
          console.error('Failed to update context:', error);
        }
      }
    };

    // Initial update
    updateContext();

    // Update every 10 seconds instead of 2 seconds to reduce load
    intervalId = setInterval(updateContext, 10000);

    // Listen for context summary generation events to refresh immediately
    const handleSummaryGenerated = () => {
      if (isMounted) {
        // Force immediate refresh when summary is generated
        updateContext();
        // Reload summaries if modal is open
        if (showSummaryModal) {
          getSummaryEntries().then(setSummaryEntries).catch(console.error);
        }
      }
    };

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.addEventListener('contextSummaryGenerated', handleSummaryGenerated);
    }

    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.removeEventListener('contextSummaryGenerated', handleSummaryGenerated);
      }
    };
  }, [showSummaryModal]);

  // Calculate percentage (use tokens as primary metric)
  const percentage = useMemo(() => {
    const tokenPercent = (contextSize.tokens / CONTEXT_LIMIT_TOKENS) * 100;
    const bytePercent = (contextSize.bytes / CONTEXT_LIMIT_BYTES) * 100;
    return Math.max(tokenPercent, bytePercent);
  }, [contextSize]);

  // Prepare chart data with gradient colors
  const chartData = useMemo(() => {
    const series: number[] = [];
    const labels: string[] = [];
    const colors: string[] = [];
    const gradientToColors: string[] = [];
    const categoryTypes: string[] = []; // Track which category each series index corresponds to

    // Add each category if it has data
    Object.entries(breakdown).forEach(([type, data]) => {
      if (data.tokens > 0) {
        series.push(data.tokens);
        const label = type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        labels.push(label);
        categoryTypes.push(type); // Store the original type for tooltip mapping

        // Base color from category
        const baseColor = CATEGORY_COLORS[type as keyof typeof CATEGORY_COLORS];
        colors.push(baseColor);

        // Gradient to color (lighter variant)
        const gradientToColor = type === 'user_interaction' ? '#D4B5D4' :
          type === 'notification' ? '#B8C5E0' :
            type === 'ai_request' ? '#A7D9EB' :
              type === 'tensorflow_request' ? '#C8B0E4' :
                '#B0C5DE';
        gradientToColors.push(gradientToColor);
      }
    });

    // If no data, show empty state
    if (series.length === 0) {
      series.push(1);
      labels.push('Empty');
      colors.push(theme ? '#e2e8f0' : '#475569');
      gradientToColors.push(theme ? '#cbd5e1' : '#64748b');
      categoryTypes.push('empty');
    }

    return { series, labels, colors, gradientToColors, categoryTypes };
  }, [breakdown, theme]);

  // Chart options with gradient fills
  const chartOptions = useMemo(() => {
    const ChartComponent = ReactApexChart || (typeof window !== 'undefined' ? (window as any).__ReactApexChart : null);
    if (Platform.OS !== 'web' || !ChartComponent) return null;

    return {
      series: chartData.series,
      chart: {
        type: 'donut' as const,
        width: size,
        height: size,
        fontFamily: 'inherit',
        toolbar: { show: false },
        animations: { enabled: true, speed: 800 },
        background: 'transparent'
      },
      labels: chartData.labels,
      colors: chartData.colors,
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'radial',
          shadeIntensity: 0.5,
          gradientToColors: chartData.gradientToColors.length > 0
            ? chartData.gradientToColors
            : chartData.colors,
          inverseColors: false,
          opacityFrom: 1,
          opacityTo: 0.7,
          stops: [0, 50, 100]
        }
      },
      plotOptions: {
        pie: {
          donut: {
            size: '70%',
            labels: {
              show: true,
              name: { show: false },
              value: {
                show: true,
                fontSize: `${Math.max(10, size / 5)}px`,
                fontWeight: 700,
                color: theme ? '#1e293b' : '#f1f5f9',
                formatter: () => `${Math.round(percentage)}%`
              },
              total: {
                show: false
              }
            }
          }
        }
      },
      dataLabels: { enabled: false },
      legend: { show: false },
      tooltip: {
        enabled: true,
        theme: theme ? 'light' : 'dark',
        style: {
          fontSize: '12px',
          fontFamily: 'inherit'
        },
        custom: ({ series, seriesIndex, w }: any) => {
          const label = w.globals.labels[seriesIndex];
          const value = series[seriesIndex];
          // Map seriesIndex to the correct category type
          const categoryType = chartData.categoryTypes[seriesIndex];
          const categoryData = categoryType && categoryType !== 'empty'
            ? breakdown[categoryType as keyof ContextBreakdown]
            : null;

          if (!categoryData) {
            return `
              <div style="padding: 12px; background: ${theme ? '#fff' : '#1e293b'}; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
                <div style="font-weight: 600; margin-bottom: 8px; color: ${theme ? '#1e293b' : '#f1f5f9'};">${label}</div>
                <div style="font-size: 12px; color: ${theme ? '#64748b' : '#94a3b8'}">
                  No data available
                </div>
              </div>
            `;
          }

          const percentageOfTotal = contextSize.tokens > 0
            ? ((categoryData.tokens / contextSize.tokens) * 100).toFixed(1)
            : '0.0';

          return `
            <div style="padding: 12px; background: ${theme ? '#fff' : '#1e293b'}; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); min-width: 200px;">
              <div style="font-weight: 600; margin-bottom: 8px; color: ${theme ? '#1e293b' : '#f1f5f9'}; font-size: 14px;">${label}</div>
              <div style="font-size: 12px; color: ${theme ? '#64748b' : '#94a3b8'}; line-height: 1.6;">
                <div style="margin-bottom: 4px;"><strong>Tokens:</strong> ${categoryData.tokens.toLocaleString()}</div>
                <div style="margin-bottom: 4px;"><strong>Bytes:</strong> ${categoryData.bytes.toLocaleString()}</div>
                <div style="margin-bottom: 4px;"><strong>Count:</strong> ${categoryData.count.toLocaleString()}</div>
                <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid ${theme ? '#e2e8f0' : '#475569'};">
                  <strong>${percentageOfTotal}%</strong> of total context
                </div>
              </div>
            </div>
          `;
        }
      },
      stroke: {
        show: true,
        width: 3,
        colors: [theme ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.3)'],
        curve: 'smooth'
      },
      states: {
        hover: {
          filter: {
            type: 'lighten',
            value: 0.15
          }
        }
      }
    };
  }, [chartData, percentage, size, theme, breakdown, contextSize, apexChartsLoaded]);

  // Format numbers for display
  const formatNumber = (num: number): string => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`;
    }
    return num.toString();
  };

  const handleDonutClick = () => {
    setShowSummaryModal(true);
  };

  const handleClearContext = async () => {
    if (isClearing) return;

    setIsClearing(true);
    try {
      await clearContext();

      // Force refresh context data (cache already invalidated by clearContext)
      const [sizeData, breakdownData, summaries] = await Promise.all([
        getContextSize(),
        getContextBreakdown(),
        getSummaryEntries()
      ]);

      setContextSize(sizeData);
      setBreakdown(breakdownData);
      setSummaryEntries(summaries);

      // Dispatch event to refresh UI
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('contextSummaryGenerated'));
      }

      // Close modal after clearing
      setShowSummaryModal(false);
    } catch (error) {
      console.error('Failed to clear context:', error);
    } finally {
      setIsClearing(false);
    }
  };

  const handleTriggerSummary = async () => {
    if (isSummarizing) return;

    try {
      const entries = await getContextEntries();
      if (entries.length === 0) {
        await showAlert('Info', 'No context entries to summarize');
        return;
      }

      setIsSummarizing(true);

      // Ensure socket listener is set up to receive summary completion notifications
      if (Platform.OS === 'web') {
        await setupSocketListener();
      }

      // Show alert that summary is being generated
      await showAlert('Generating Summary', 'This may take a moment...');

      // Call the API - it returns { success: true, status: 'queued' }
      // The actual summary will be processed via socket notification
      const response = await apis.summarizeContext(entries);

      if (!response.success) {
        throw new Error(response.error || 'Failed to queue summary generation');
      }

      // The summary processing happens asynchronously via socket notification
      // The socket handler will call processSummaryResponse which clears context and saves summary
      await showAlert('Summary Generation Queued', 'The summary is being generated in the background. You will be notified when it completes.');

      // Refresh stats after a short delay to allow socket notification to process
      // The event listener will also refresh when the summary is ready
      setTimeout(async () => {
        try {
          const [sizeData, breakdownData, summaries] = await Promise.all([
            getContextSize(),
            getContextBreakdown(),
            getSummaryEntries()
          ]);
          setContextSize(sizeData);
          setBreakdown(breakdownData);
          setSummaryEntries(summaries);
        } catch (error) {
          console.error('Failed to refresh context after summary:', error);
        }
      }, 2000);
    } catch (error: any) {
      console.error('Failed to generate summary:', error);
      const errorMessage = error?.requiresPremium
        ? 'Premium subscription required for context summarization'
        : 'Failed to generate summary';
      await showAlert('Error', errorMessage);
    } finally {
      setIsSummarizing(false);
    }
  };

  // Check if we should show fallback (native or ApexCharts not loaded)
  const shouldShowFallback = Platform.OS !== 'web' || !ReactApexChart || !chartOptions;

  if (shouldShowFallback && Platform.OS !== 'web') {
    // Native fallback
    return (
      <Pressable onPress={handleDonutClick}>
        <View style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: theme ? '#e2e8f0' : '#475569',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Text style={{
            fontSize: size / 4,
            fontWeight: '700',
            color: theme ? '#1e293b' : '#f1f5f9'
          }}>
            {Math.round(percentage)}%
          </Text>
        </View>
      </Pressable>
    );
  }

  // Calculate detailed stats for tooltip
  const totalEntries = useMemo(() => {
    return Object.values(breakdown).reduce((sum, data) => sum + data.count, 0);
  }, [breakdown]);

  const tokenPercentage = useMemo(() => {
    return ((contextSize.tokens / CONTEXT_LIMIT_TOKENS) * 100).toFixed(1);
  }, [contextSize.tokens]);

  const bytePercentage = useMemo(() => {
    return ((contextSize.bytes / CONTEXT_LIMIT_BYTES) * 100).toFixed(1);
  }, [contextSize.bytes]);

  // Create enhanced tooltip content for web
  const tooltipContent = Platform.OS === 'web' ? (
    <div style={{
      padding: '16px',
      background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.98) 0%, rgba(15, 23, 42, 0.98) 100%)',
      borderRadius: '12px',
      minWidth: '280px',
      maxWidth: '360px',
      border: '1px solid rgba(200, 162, 200, 0.2)',
      boxShadow: '0 12px 32px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(135, 206, 235, 0.1)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px',
        paddingBottom: '12px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          background: 'linear-gradient(135deg, #C8A2C8 0%, #87CEEB 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '16px'
        }}>
          📊
        </div>
        <div>
          <div style={{ fontWeight: '700', color: 'white', fontSize: '15px', lineHeight: '1.2' }}>
            Context Window
          </div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginTop: '2px' }}>
            {totalEntries} {totalEntries === 1 ? 'entry' : 'entries'}
          </div>
        </div>
      </div>

      {/* Usage Stats */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
        {/* Tokens Progress */}
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '6px'
          }}>
            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)', fontWeight: '500' }}>
              Tokens
            </span>
            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)', fontWeight: '600' }}>
              {formatNumber(contextSize.tokens)} / {formatNumber(CONTEXT_LIMIT_TOKENS)} ({tokenPercentage}%)
            </span>
          </div>
          <div style={{
            width: '100%',
            height: '6px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${Math.min(100, parseFloat(tokenPercentage))}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #C8A2C8 0%, #87CEEB 100%)',
              borderRadius: '3px',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        {/* Bytes Progress */}
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '6px'
          }}>
            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)', fontWeight: '500' }}>
              Bytes
            </span>
            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)', fontWeight: '600' }}>
              {formatNumber(contextSize.bytes)} / {formatNumber(CONTEXT_LIMIT_BYTES)} ({bytePercentage}%)
            </span>
          </div>
          <div style={{
            width: '100%',
            height: '6px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${Math.min(100, parseFloat(bytePercentage))}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #87CEEB 0%, #C8A2C8 100%)',
              borderRadius: '3px',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
      </div>

      {/* Breakdown by Category */}
      <div style={{
        marginTop: '12px',
        paddingTop: '12px',
        borderTop: '1px solid rgba(255,255,255,0.1)'
      }}>
        <div style={{
          fontSize: '11px',
          color: 'rgba(255,255,255,0.6)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          fontWeight: '600',
          marginBottom: '10px'
        }}>
          Breakdown
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {Object.entries(breakdown)
            .filter(([_, data]) => data.tokens > 0)
            .sort(([_, a], [__, b]) => b.tokens - a.tokens)
            .map(([type, data]) => {
              const categoryPercentage = contextSize.tokens > 0
                ? ((data.tokens / contextSize.tokens) * 100).toFixed(1)
                : '0.0';
              return (
                <div
                  key={type}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px',
                    borderRadius: '6px',
                    backgroundColor: 'rgba(255,255,255,0.03)',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.08)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.03)';
                  }}
                >
                  <div style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: CATEGORY_COLORS[type as keyof typeof CATEGORY_COLORS],
                    flexShrink: 0,
                    boxShadow: `0 0 8px ${CATEGORY_COLORS[type as keyof typeof CATEGORY_COLORS]}40`
                  }} />
                  <span style={{
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.9)',
                    flex: 1,
                    fontWeight: '500'
                  }}>
                    {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                    <span style={{
                      fontSize: '12px',
                      color: 'rgba(255,255,255,0.9)',
                      fontWeight: '600'
                    }}>
                      {formatNumber(data.tokens)}k
                    </span>
                    <span style={{
                      fontSize: '10px',
                      color: 'rgba(255,255,255,0.5)'
                    }}>
                      {categoryPercentage}%
                    </span>
                  </div>
                </div>
              );
            })}
          {Object.values(breakdown).every(data => data.tokens === 0) && (
            <div style={{
              fontSize: '12px',
              color: 'rgba(255,255,255,0.5)',
              fontStyle: 'italic',
              textAlign: 'center',
              padding: '8px'
            }}>
              No context data yet
            </div>
          )}
        </div>
      </div>
    </div>
  ) : (
    <View style={{
      padding: 16,
      backgroundColor: 'rgba(15, 23, 42, 0.98)',
      borderRadius: 12,
      minWidth: 280,
      borderWidth: 1,
      borderColor: 'rgba(200, 162, 200, 0.2)'
    }}>
      <View style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 12,
        paddingBottom: 12,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(255, 255, 255, 0.1)'
      }}>
        <View style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          backgroundColor: '#C8A2C8',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Text style={{ fontSize: 16 }}>📊</Text>
        </View>
        <View>
          <Text style={{ fontWeight: '700', color: 'white', fontSize: 15 }}>
            Context Window
          </Text>
          <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
            {totalEntries} {totalEntries === 1 ? 'entry' : 'entries'}
          </Text>
        </View>
      </View>

      <View style={{ gap: 10, marginBottom: 12 }}>
        <View>
          <View style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 6
          }}>
            <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.9)', fontWeight: '500' }}>
              Tokens
            </Text>
            <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.9)', fontWeight: '600' }}>
              {formatNumber(contextSize.tokens)} / {formatNumber(CONTEXT_LIMIT_TOKENS)} ({tokenPercentage}%)
            </Text>
          </View>
          <View style={{
            width: '100%',
            height: 6,
            backgroundColor: 'rgba(255,255,255,0.1)',
            borderRadius: 3,
            overflow: 'hidden'
          }}>
            <View style={{
              width: `${Math.min(100, parseFloat(tokenPercentage))}%`,
              height: '100%',
              backgroundColor: '#C8A2C8',
              borderRadius: 3
            }} />
          </View>
        </View>

        <View>
          <View style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 6
          }}>
            <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.9)', fontWeight: '500' }}>
              Bytes
            </Text>
            <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.9)', fontWeight: '600' }}>
              {formatNumber(contextSize.bytes)} / {formatNumber(CONTEXT_LIMIT_BYTES)} ({bytePercentage}%)
            </Text>
          </View>
          <View style={{
            width: '100%',
            height: 6,
            backgroundColor: 'rgba(255,255,255,0.1)',
            borderRadius: 3,
            overflow: 'hidden'
          }}>
            <View style={{
              width: `${Math.min(100, parseFloat(bytePercentage))}%`,
              height: '100%',
              backgroundColor: '#87CEEB',
              borderRadius: 3
            }} />
          </View>
        </View>
      </View>

      <View style={{
        marginTop: 12,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: 'rgba(255,255,255,0.1)'
      }}>
        <Text style={{
          fontSize: 11,
          color: 'rgba(255,255,255,0.6)',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          fontWeight: '600',
          marginBottom: 10
        }}>
          Breakdown
        </Text>
        <View style={{ gap: 6 }}>
          {Object.entries(breakdown)
            .filter(([_, data]) => data.tokens > 0)
            .sort(([_, a], [__, b]) => b.tokens - a.tokens)
            .map(([type, data]) => {
              const categoryPercentage = contextSize.tokens > 0
                ? ((data.tokens / contextSize.tokens) * 100).toFixed(1)
                : '0.0';
              return (
                <View
                  key={type}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                    padding: 6,
                    borderRadius: 6,
                    backgroundColor: 'rgba(255,255,255,0.03)'
                  }}
                >
                  <View style={{
                    width: 10,
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: CATEGORY_COLORS[type as keyof typeof CATEGORY_COLORS]
                  }} />
                  <Text style={{
                    fontSize: 12,
                    color: 'rgba(255,255,255,0.9)',
                    flex: 1,
                    fontWeight: '500'
                  }}>
                    {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </Text>
                  <View style={{ alignItems: 'flex-end' }}>
                    <Text style={{
                      fontSize: 12,
                      color: 'rgba(255,255,255,0.9)',
                      fontWeight: '600'
                    }}>
                      {formatNumber(data.tokens)}k
                    </Text>
                    <Text style={{
                      fontSize: 10,
                      color: 'rgba(255,255,255,0.5)'
                    }}>
                      {categoryPercentage}%
                    </Text>
                  </View>
                </View>
              );
            })}
          {Object.values(breakdown).every(data => data.tokens === 0) && (
            <Text style={{
              fontSize: 12,
              color: 'rgba(255,255,255,0.5)',
              fontStyle: 'italic',
              textAlign: 'center',
              padding: 8
            }}>
              No context data yet
            </Text>
          )}
        </View>
      </View>
    </View>
  );

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <>
      <HoverLabel
        label={tooltipContent}
        position="bottom"
        onChildClick={handleDonutClick}
      >
        {Platform.OS === 'web' ? (
          <div
            data-clickable="true"
            style={{
              cursor: 'pointer',
              userSelect: 'none',
              display: 'inline-block'
            } as React.CSSProperties}
          >
            <LinearGradient
              colors={['#C8A2C8', '#87CEEB']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={{
                width: size + 8,
                height: size + 8,
                borderRadius: (size + 8) / 2,
                padding: 4,
                borderWidth: 1,
                transform: [{scale: 0.8}],
                borderColor: theme
                  ? 'rgba(200, 162, 200, 0.3)'
                  : 'rgba(135, 206, 235, 0.3)',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: theme ? 0.1 : 0.3,
                shadowRadius: 8,
                elevation: 6,
                ...(Platform.OS === 'web' && {
                  backdropFilter: 'blur(10px)',
                } as any),
              }}
            >
              {/* Floating blur elements for depth */}
              <View style={{
                position: 'absolute',
                top: '20%',
                left: '20%',
                width: size * 0.4,
                height: size * 0.4,
                backgroundColor: theme ? 'rgba(59, 130, 246, 0.15)' : 'rgba(147, 197, 253, 0.1)',
                borderRadius: size * 0.2,
                filter: 'blur(20px)',
                zIndex: 0,
              }} />
              <View style={{
                width: size,
                height: size,
                borderRadius: size / 2,
                backgroundColor: theme ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)',
                ...(Platform.OS === 'web' && {
                  backdropFilter: 'blur(8px)',
                } as any),
                zIndex: 1,
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden'
              }}>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDonutClick();
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  data-clickable="true"
                  style={{
                    width: size,
                    height: size,
                    position: 'relative',
                    zIndex: 2,
                    pointerEvents: 'auto',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transform: 'scale(1.2)'
                  }}
                >
                  {(() => {
                    const ChartComponent = ReactApexChart || (typeof window !== 'undefined' ? (window as any).__ReactApexChart : null);
                    if (chartOptions && ChartComponent) {
                      return (
                        <ChartComponent
                          options={chartOptions}
                          series={chartOptions.series || []}
                          type="donut"
                          width={size}
                          height={size}
                        />
                      );
                    }
                    // Fallback view when ApexCharts not available
                    return (
                      <div style={{
                        width: size,
                        height: size,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: `${size / 4}px`,
                        fontWeight: '700',
                        color: theme ? '#1e293b' : '#f1f5f9'
                      }}>
                        {Math.round(percentage)}%
                      </div>
                    );
                  })()}
                </div>
              </View>
            </LinearGradient>
          </div>
        ) : (
          <View>
            <LinearGradient
              colors={['#C8A2C8', '#87CEEB']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={{
                width: size + 8,
                height: size + 8,
                borderRadius: (size + 8) / 2,
                padding: 4,
                borderWidth: 1,
                borderColor: theme
                  ? 'rgba(200, 162, 200, 0.3)'
                  : 'rgba(135, 206, 235, 0.3)',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: theme ? 0.1 : 0.3,
                shadowRadius: 8,
                elevation: 6,
              }}
            >
              <View style={{
                width: size,
                height: size,
                borderRadius: size / 2,
                backgroundColor: theme ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)',
                zIndex: 1,
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden'
              }}>
                <Pressable onPress={handleDonutClick} style={{ width: size, height: size, justifyContent: 'center', alignItems: 'center' }}>
                  <Text style={{
                    fontSize: size / 4,
                    fontWeight: '700',
                    color: theme ? '#1e293b' : '#f1f5f9'
                  }}>
                    {Math.round(percentage)}%
                  </Text>
                </Pressable>
              </View>
            </LinearGradient>
          </View>
        )}
      </HoverLabel>

      {/* Summary Modal */}
      <Dialog modal open={showSummaryModal} onOpenChange={setShowSummaryModal}>
        <Adapt platform="touch">
          <Sheet animation="medium" zIndex={200000} modal dismissOnSnapToBottom>
            <Sheet.Frame
              padding="$4"
              gap="$4"
              backgroundColor={theme ? '#ffffff' : '#1e293b'}
              maxHeight="80%"
            >
              <Adapt.Contents />
            </Sheet.Frame>
            <Sheet.Overlay
              animation="lazy"
              enterStyle={{ opacity: 0 }}
              exitStyle={{ opacity: 0 }}
            />
          </Sheet>
        </Adapt>
        <Dialog.Portal>
          <Dialog.Overlay
            animation="quick"
            opacity={0.5}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
          <Dialog.Content
            bordered
            elevate
            animation={[
              'quick',
              {
                opacity: {
                  overshootClamping: true,
                },
              },
            ]}
            enterStyle={{ x: 0, y: -20, opacity: 0 }}
            exitStyle={{ x: 0, y: 10, opacity: 0 }}
            maxWidth="90%"
            width="90%"
            maxHeight="80%"
            backgroundColor={theme ? '#ffffff' : '#1e293b'}
          >
            <YStack gap="$3" padding="$3">
              <XStack alignItems="center" justifyContent="space-between" marginBottom="$2">
                <Dialog.Title
                  style={{
                    color: theme ? '#1e293b' : '#f1f5f9',
                    fontSize: 20,
                    fontWeight: '700'
                  }}
                >
                  Context Summaries
                </Dialog.Title>
                <XStack gap="$2" alignItems="center">
                  <Button
                    size="$3"
                    onPress={handleTriggerSummary}
                    disabled={isSummarizing || isClearing}
                    backgroundColor={theme ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.2)'}
                    borderWidth={1}
                    borderColor={theme ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.4)'}
                    opacity={isSummarizing || isClearing ? 0.6 : 1}
                  >
                    <Text style={{
                      color: '#3b82f6',
                      fontSize: 13,
                      fontWeight: '600'
                    }}>
                      {isSummarizing ? 'Generating...' : 'Generate Summary'}
                    </Text>
                  </Button>
                  <Button
                    size="$3"
                    onPress={handleClearContext}
                    disabled={isClearing || isSummarizing}
                    backgroundColor={theme ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.2)'}
                    borderWidth={1}
                    borderColor={theme ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.4)'}
                    opacity={isClearing || isSummarizing ? 0.6 : 1}
                  >
                    <Text style={{
                      color: '#ef4444',
                      fontSize: 13,
                      fontWeight: '600'
                    }}>
                      {isClearing ? 'Clearing...' : 'Clear Context'}
                    </Text>
                  </Button>
                  <Button
                    size="$2"
                    circular
                    onPress={() => setShowSummaryModal(false)}
                    backgroundColor={theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)'}
                  >
                    <Text style={{ color: theme ? '#1e293b' : '#f1f5f9', fontSize: 18 }}>✕</Text>
                  </Button>
                </XStack>
              </XStack>

              {summaryEntries.length === 0 ? (
                <YStack alignItems="center" justifyContent="center" padding="$6" gap="$3">
                  <Text style={{
                    fontSize: 48,
                    marginBottom: 8
                  }}>📝</Text>
                  <Text style={{
                    color: theme ? '#64748b' : '#94a3b8',
                    fontSize: 16,
                    textAlign: 'center'
                  }}>
                    No summaries generated yet
                  </Text>
                  <Text style={{
                    color: theme ? '#94a3b8' : '#64748b',
                    fontSize: 12,
                    textAlign: 'center',
                    marginTop: 4
                  }}>
                    Summaries are automatically created when your context reaches 200k tokens
                  </Text>
                </YStack>
              ) : (
                <ScrollView
                  style={Platform.OS === 'web' ? { maxHeight: '60vh' } as any : { maxHeight: 400 }}
                  showsVerticalScrollIndicator={true}
                >
                  <YStack gap="$3">
                    {summaryEntries.map((entry, index) => {
                      const metadata = entry.metadata || {};
                      const originalCount = metadata.originalEntryCount || 0;
                      const reduction = metadata.reductionPercentage || 0;
                      const originalTokens = metadata.originalTokenCount || 0;
                      const summaryTokens = metadata.summaryTokenCount || entry.tokenCount;

                      return (
                        <View
                          key={entry.id || index}
                          style={{
                            padding: 16,
                            borderRadius: 12,
                            backgroundColor: theme ? 'rgba(200, 162, 200, 0.1)' : 'rgba(135, 206, 235, 0.1)',
                            borderWidth: 1,
                            borderColor: theme ? 'rgba(200, 162, 200, 0.3)' : 'rgba(135, 206, 235, 0.3)',
                            marginBottom: 12
                          }}
                        >
                          {/* Header */}
                          <XStack alignItems="center" justifyContent="space-between" marginBottom="$2">
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                              <View style={{
                                width: 8,
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: '#87CEEB'
                              }} />
                              <Text style={{
                                color: theme ? '#1e293b' : '#f1f5f9',
                                fontSize: 14,
                                fontWeight: '600'
                              }}>
                                Summary #{summaryEntries.length - index}
                              </Text>
                            </View>
                            <Text style={{
                              color: theme ? '#64748b' : '#94a3b8',
                              fontSize: 11
                            }}>
                              {formatDate(entry.timestamp)}
                            </Text>
                          </XStack>

                          {/* Stats */}
                          <XStack gap="$3" marginBottom="$3" flexWrap="wrap">
                            <View style={{
                              padding: 8,
                              borderRadius: 8,
                              backgroundColor: theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                              minWidth: 100
                            }}>
                              <Text style={{
                                color: theme ? '#64748b' : '#94a3b8',
                                fontSize: 10,
                                marginBottom: 2
                              }}>
                                Original Entries
                              </Text>
                              <Text style={{
                                color: theme ? '#1e293b' : '#f1f5f9',
                                fontSize: 16,
                                fontWeight: '700'
                              }}>
                                {originalCount.toLocaleString()}
                              </Text>
                            </View>
                            <View style={{
                              padding: 8,
                              borderRadius: 8,
                              backgroundColor: theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                              minWidth: 100
                            }}>
                              <Text style={{
                                color: theme ? '#64748b' : '#94a3b8',
                                fontSize: 10,
                                marginBottom: 2
                              }}>
                                Reduction
                              </Text>
                              <Text style={{
                                color: '#10b981',
                                fontSize: 16,
                                fontWeight: '700'
                              }}>
                                {reduction.toFixed(1)}%
                              </Text>
                            </View>
                            <View style={{
                              padding: 8,
                              borderRadius: 8,
                              backgroundColor: theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                              minWidth: 100
                            }}>
                              <Text style={{
                                color: theme ? '#64748b' : '#94a3b8',
                                fontSize: 10,
                                marginBottom: 2
                              }}>
                                Tokens
                              </Text>
                              <Text style={{
                                color: theme ? '#1e293b' : '#f1f5f9',
                                fontSize: 16,
                                fontWeight: '700'
                              }}>
                                {formatNumber(originalTokens)}k → {formatNumber(summaryTokens)}k
                              </Text>
                            </View>
                          </XStack>

                          {/* Content */}
                          <View style={{
                            padding: 12,
                            borderRadius: 8,
                            backgroundColor: theme ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.05)',
                            maxHeight: 200
                          }}>
                            <ScrollView
                              nestedScrollEnabled
                              style={{ maxHeight: 200 }}
                            >
                              <Text style={{
                                color: theme ? '#1e293b' : '#f1f5f9',
                                fontSize: 13,
                                lineHeight: 20
                              }}>
                                {entry.content}
                              </Text>
                            </ScrollView>
                          </View>
                        </View>
                      );
                    })}
                  </YStack>
                </ScrollView>
              )}
            </YStack>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
    </>
  );
};

export default ContextProgressRing;

